import {
  addDoc,
  arrayRemove,
  arrayUnion,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore"
import {
  customerDoc,
  getDb,
  sessionDoc,
  sessionNotesDoc,
  sessionsCollection,
  toCustomer,
  toSession,
  toSessionNotes,
} from "@/lib/firebase/firestore"
import {
  DEFAULT_SESSION_CONFIG,
  DEFAULT_SESSION_LIFETIME_MS,
  INITIAL_TIMER_STATE,
  NOT_REMOVED,
} from "@/types"
import { expiryFromNow } from "@/utils/sessionLifecycle"
import { pendingAsNow } from "@/utils/timestamps"
import type {
  OverlayKind,
  Phase,
  Round,
  Session,
  SessionConfig,
  SessionNotes,
  SessionStatus,
  RemovalKind,
  TimerState,
} from "@/types"

/*
 * Session reads and writes.
 *
 * WHO MAY CALL WHAT — enforced by the Firestore rules as well as by the UI.
 *
 *   Facilitators of the session (session.facilitatorIds):
 *     createSession, updateSessionConfig, setFamilyInPlay,
 *     setRoundAndPhase, startSession, completeSession, extendSession, saveAndPause,
 *     startTimer, pauseTimer, resumeTimer, showOverlay, setOverlaySelection,
 *     advanceFromOverlay, getSessionNotes, setSessionNote,
 *     setKnownPains, listSessionsForFacilitator, removeSession, restoreSession
 *
 *   Anyone holding the link: getSession
 *
 * Gate the facilitator-only calls on `selectIsFacilitator`.
 *
 * There is no join code. The session id IS the share link: a second identifier
 * meant a second lookup, a uniqueness retry loop on every create, and a way for
 * the two to disagree.
 */

/**
 * Creates a session for a customer, already open to the room.
 *
 * The customer's membership and name are copied onto the session because that is
 * what the rules and the room's screens read — see `syncCustomerToSessions`. The
 * customer is read first for exactly that reason: a session created without
 * `facilitatorIds` is one nobody can drive.
 *
 * It starts `pending`, not `draft`: the link works from this moment, guests can
 * take their seats and say they are ready, and "started" is the separate thing
 * the facilitator does when the room is full.
 */
export async function createSession(
  customerId: string,
  name: string,
  config: Partial<SessionConfig> = {},
  lifetimeMs: number = DEFAULT_SESSION_LIFETIME_MS
): Promise<string> {
  const customer = toCustomer(await getDoc(customerDoc(customerId)))
  if (!customer) throw new Error(`Customer ${customerId} does not exist`)

  const ref = await addDoc(sessionsCollection(), {
    customerId,
    customerName: customer.name,
    facilitatorIds: customer.facilitatorIds,
    name,
    status: "pending" satisfies SessionStatus,
    round: "lobby" satisfies Round,
    phase: "setup" satisfies Phase,
    currentJobId: null,
    overlay: null,
    knownPains: [],
    config: {
      ...DEFAULT_SESSION_CONFIG,
      ...config,
      roundDurations: {
        ...DEFAULT_SESSION_CONFIG.roundDurations,
        ...(config.roundDurations ?? {}),
      },
    },
    timer: INITIAL_TIMER_STATE,
    startedAt: null,
    completedAt: null,
    pausedAt: null,
    expiresAt: expiryFromNow(lifetimeMs),
    ...NOT_REMOVED,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function getSession(sessionId: string): Promise<Session | null> {
  return toSession(await getDoc(sessionDoc(sessionId)))
}

/**
 * Every session this facilitator may see, newest first.
 *
 * Scoped by the membership copied onto each session, which is what replaced the
 * old "read every session in the project" list. That call was unbounded and told
 * every facilitator about every workshop; this one returns their own and is
 * bounded by their own history.
 *
 * Still sorted client-side: `createdAt` is a `serverTimestamp()` that reads null
 * until acknowledged, and Firestore omits documents missing the field it is
 * ordered by — so `orderBy("createdAt")` would drop the newest session outright.
 *
 * Archived and deleted sessions come back in it, for the reason
 * `listCustomersForFacilitator` spells out: the Archive reads the same list, and
 * this is the only session query the rules admit. `useFacilitatorIndex` is where
 * the two lists part company.
 */
export async function listSessionsForFacilitator(facilitatorId: string): Promise<Session[]> {
  const snapshot = await getDocs(
    query(sessionsCollection(), where("facilitatorIds", "array-contains", facilitatorId))
  )
  return snapshot.docs
    .map(toSession)
    .filter((s): s is Session => s !== null)
    .sort((a, b) => pendingAsNow(b.createdAt) - pendingAsNow(a.createdAt))
}

/**
 * `roundDurations` is patchable one round at a time, which `Partial<SessionConfig>`
 * cannot say — `Partial` stops at the top level, so its `roundDurations` is still
 * the complete `Record<Round, number>`. The loop below has always written whatever
 * subset it was given; this is the type that admits it. A full config still
 * satisfies it, so every existing caller is unaffected.
 */
export type SessionConfigPatch = Omit<Partial<SessionConfig>, "roundDurations"> & {
  roundDurations?: Partial<Record<Round, number>>
}

/**
 * Written with dot-notation paths because `updateDoc` replaces a nested map
 * wholesale — passing `{ config: partial }` would drop the untouched keys.
 */
export async function updateSessionConfig(
  sessionId: string,
  patch: SessionConfigPatch
): Promise<void> {
  const payload: Record<string, unknown> = { updatedAt: serverTimestamp() }

  for (const [key, value] of Object.entries(patch)) {
    if (key === "roundDurations" || value === undefined) continue
    payload[`config.${key}`] = value
  }
  for (const [round, minutes] of Object.entries(patch.roundDurations ?? {})) {
    if (minutes === undefined) continue
    payload[`config.roundDurations.${round}`] = minutes
  }

  await updateDoc(sessionDoc(sessionId), payload)
}

/**
 * Ticks one family in or out of the deck.
 *
 * A server-side array transform, not a read-modify-write of `config.families`.
 * The picker can only compute `[...families, n]` from the last snapshot the
 * listener delivered, so ticking two families quickly writes the second on top
 * of a config that has never heard of the first — the same "racing your own
 * echo" bug the card picks avoid by adding one card at a time.
 *
 * Order is not preserved by `arrayUnion`, and nothing reads it: every consumer
 * asks `includes`, and deal order comes from `familiesInPlay`.
 */
export async function setFamilyInPlay(
  sessionId: string,
  family: number,
  inPlay: boolean
): Promise<void> {
  await updateDoc(sessionDoc(sessionId), {
    "config.families": inPlay ? arrayUnion(family) : arrayRemove(family),
    updatedAt: serverTimestamp(),
  })
}

/**
 * Replaces the loaded pain points. The whole array goes at once because the
 * dialog edits, adds and removes in one sitting, and a per-index path would
 * leave a hole behind whenever a note in the middle is dropped. Named for the
 * field rather than `notes`, which on a session means the facilitator's own.
 *
 * These are configuration, not yet pain points: `seedKnownPains` turns them into
 * documents when the Pains round opens, because a pain must have an id before
 * anyone can vote for it.
 */
export async function setKnownPains(sessionId: string, knownPains: string[]): Promise<void> {
  await updateDoc(sessionDoc(sessionId), { knownPains, updatedAt: serverTimestamp() })
}

/* ---------------------------------------------------------------- lifecycle */

/**
 * Starts the flow, and moves the room off the lobby in the SAME write.
 *
 * `status` is what participants read to know the workshop is under way, since
 * the facilitator has no presence in Firestore to infer it from — and the round
 * is what they read to know where it is. Written separately they land as two
 * snapshots, so every other screen would show a started session still sitting in
 * the lobby before jumping, or worse, a lobby that says the session is running.
 *
 * `startedAt` is stamped here rather than at creation: a session can sit
 * `pending` for a day while people gather, and creation time would make every
 * report claim the workshop ran that long.
 */
export async function startSession(sessionId: string, round: Round, phase: Phase): Promise<void> {
  await writePosition(sessionId, round, phase, {
    status: "active" satisfies SessionStatus,
    startedAt: serverTimestamp(),
  })
}

/**
 * Closes the session for good. Nothing more is collected; everyone keeps read
 * access so the room can look through the result and export it.
 *
 * This is the deliberate way out. The other is `expiresAt` simply passing, which
 * nobody writes — see `utils/sessionLifecycle`.
 */
export async function completeSession(sessionId: string): Promise<void> {
  await updateDoc(sessionDoc(sessionId), {
    status: "completed" satisfies SessionStatus,
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}

/**
 * Pushes the deadline out, from NOW rather than from the old deadline.
 *
 * A workshop that ran over is extended because it is still in the room, so the
 * facilitator wants another full window — not the remainder of a window that has
 * already lapsed.
 */
export async function extendSession(
  sessionId: string,
  lifetimeMs: number = DEFAULT_SESSION_LIFETIME_MS
): Promise<void> {
  await updateDoc(sessionDoc(sessionId), {
    expiresAt: expiryFromNow(lifetimeMs),
    updatedAt: serverTimestamp(),
  })
}

/**
 * Save & quit: parks a running session so it can be resumed whenever.
 *
 * One write — mark it paused for the lists, CLEAR the deadline so it never expires
 * on its own (a `null` `expiresAt` is "no deadline" to `isExpired`/`isLive`), and
 * reset the round clock to idle so it does not read "Over" when the room comes
 * back. A saved session stays open until it is picked up again or archived; the
 * 24-hour window only ever governed sessions nobody parked. It stays `active` at
 * its current round, so reopening it resumes exactly where it stopped, and
 * `pausedAt` clears the moment the room moves again (`writePosition`) or the clock
 * is started (`startTimer`).
 *
 * Only valid while the session is live: once it has expired the rules allow no
 * write but extending `expiresAt`, which is why Save & quit is hidden then — see
 * `RoundControls`.
 */
export async function saveAndPause(sessionId: string): Promise<void> {
  await updateDoc(sessionDoc(sessionId), {
    pausedAt: serverTimestamp(),
    expiresAt: null,
    timer: INITIAL_TIMER_STATE,
    updatedAt: serverTimestamp(),
  })
}

/* ------------------------------------------------------------------ overlays */

/**
 * Raises the shared interstitial. Written to the session rather than held in
 * the facilitator's client so participants see the curtain and can be told to
 * wait, instead of staring at a round that has already moved on.
 */
export async function showOverlay(sessionId: string, kind: OverlayKind): Promise<void> {
  await updateDoc(sessionDoc(sessionId), {
    overlay: { kind, selection: null },
    updatedAt: serverTimestamp(),
  })
}

/**
 * Highlights a choice inside an overlay (the pain picked on `jobPick` or the
 * job gate — both ask which problem becomes the next job).
 *
 * A no-op when no overlay is up. The dot-notation path creates what it needs, so
 * writing it blind against a null overlay produced `{ selection }` with no
 * `kind` — a document the mapper reads as "no overlay" (correctly) while the
 * stored data says otherwise, which is exactly the kind of corruption that
 * surfaces much later as an overlay that cannot be dismissed. Transactional
 * rather than a read-then-write, so a dismissal landing in between cannot
 * resurrect the overlay.
 */
export async function setOverlaySelection(
  sessionId: string,
  selection: string | null
): Promise<void> {
  await runTransaction(getDb(), async (tx) => {
    const ref = sessionDoc(sessionId)
    const snapshot = await tx.get(ref)
    if (!snapshot.exists()) throw new Error(`Session ${sessionId} does not exist`)
    if (toSession(snapshot)!.overlay === null) return

    tx.update(ref, { "overlay.selection": selection, updatedAt: serverTimestamp() })
  })
}

/**
 * Drops the curtain and moves the room on, in ONE write.
 *
 * Every curtain exists to introduce what comes next — ready opens the Pains
 * round, deck opens the Cards round, wrap opens the report — so dismissing it is
 * always the same action as changing round. Written separately, the two land as
 * two snapshots: every other client would show the previous round with the
 * curtain already gone before jumping. One update means no client can observe
 * the in-between state.
 */
export async function advanceFromOverlay(
  sessionId: string,
  round: Round,
  phase: Phase
): Promise<void> {
  await writePosition(sessionId, round, phase, { overlay: null })
}

/* -------------------------------------------------------------------- notes */

/**
 * The facilitator's own notes, read from `sessions/{id}/private/notes`.
 *
 * Not on the session document, and not in the store either. The session is
 * world-readable — its id is the share link, and Firestore has no field-level
 * reads — so a note kept there is public to the room it is written about, which
 * is the opposite of what the notes box promises. Nothing else needs them, so
 * they are fetched by the one panel that shows them rather than subscribed to:
 * a listener opened for every visitor would fail for guests and report the
 * session's connection dead.
 */
export async function getSessionNotes(sessionId: string): Promise<SessionNotes> {
  return toSessionNotes(await getDoc(sessionNotesDoc(sessionId)))
}

/**
 * One round's note. `setDoc` with `merge`, not `updateDoc`: the document does
 * not exist until the first note is written, and merge both creates it and
 * leaves the other rounds' notes alone.
 */
export async function setSessionNote(sessionId: string, round: Round, text: string): Promise<void> {
  await setDoc(sessionNotesDoc(sessionId), { [round]: text }, { merge: true })
}

/**
 * Moves the room, and puts the clock back to idle whenever the ROUND changes.
 *
 * The timer belongs to the round the facilitator started it for. Left alone, the
 * Pains clock carries on into the Job round and reads "Over" during a round
 * planned for a different length — and worse, `SessionTimer` has no path from
 * `running`/`paused` back to `idle`, so every later round would inherit a clock
 * it cannot restart and the ± buttons would edit the old round's time instead of
 * the plan.
 *
 * One write, so no client sees the new round with the old round's countdown. A
 * transaction because the decision depends on the round being replaced, and only
 * the document knows that — the caller's snapshot may be a round behind.
 *
 * Phase changes inside a round leave the clock alone: the room is still in the
 * beat it was timed for.
 */
async function writePosition(
  sessionId: string,
  round: Round,
  phase: Phase,
  extra: Record<string, unknown> = {}
): Promise<void> {
  await runTransaction(getDb(), async (tx) => {
    const ref = sessionDoc(sessionId)
    const snapshot = await tx.get(ref)
    if (!snapshot.exists()) throw new Error(`Session ${sessionId} does not exist`)

    const leavingRound = snapshot.get("round") !== round

    tx.update(ref, {
      ...extra,
      round,
      phase,
      ...(leavingRound ? { timer: INITIAL_TIMER_STATE } : {}),
      // Moving the room is resuming it: a parked session (Save & quit) comes back
      // to Live the instant the facilitator steps it forward or back.
      pausedAt: null,
      updatedAt: serverTimestamp(),
    })
  })
}

export async function setRoundAndPhase(
  sessionId: string,
  round: Round,
  phase: Phase
): Promise<void> {
  await writePosition(sessionId, round, phase)
}

/* -------------------------------------------------------------------- timer */

/**
 * Starts a fresh timer. The timer is written as a whole map: it is small and
 * always changes as a unit. `startedAt` uses a server timestamp so every client
 * derives remaining time from the same origin instead of trusting its own clock.
 *
 * ALWAYS restarts from zero, deliberately — this is what a new round wants, even
 * when the round it replaces had the same duration. So a play button on a paused
 * timer must call `resumeTimer`; calling this instead would discard the time
 * already run. Only `resumeTimer` is safe to fire at a running timer.
 */
export async function startTimer(sessionId: string, durationMs: number): Promise<void> {
  await updateDoc(sessionDoc(sessionId), {
    timer: { status: "running", startedAt: serverTimestamp(), durationMs, elapsedMs: 0 },
    // Starting a round's clock is resuming a parked session — see `saveAndPause`.
    pausedAt: null,
    updatedAt: serverTimestamp(),
  })
}

/**
 * Reads the timer from the session document inside a transaction.
 *
 * Pause and resume are read-modify-write: they carry `durationMs` and
 * `elapsedMs` forward. Taking those from the caller means taking them from
 * whatever the caller last rendered, and a facilitator whose screen has not yet
 * received the newest snapshot would write stale values back over the current
 * ones — pausing after a round change restored the PREVIOUS round's duration for
 * every client. The transaction reads the values it carries forward, so the
 * write is either based on current state or retried.
 */
async function updateTimer(
  sessionId: string,
  next: (timer: TimerState) => Record<string, unknown> | null
): Promise<void> {
  await runTransaction(getDb(), async (tx) => {
    const ref = sessionDoc(sessionId)
    const snapshot = await tx.get(ref)
    if (!snapshot.exists()) throw new Error(`Session ${sessionId} does not exist`)

    // Mapped through `toSession` so the transaction reads the same validated,
    // epoch-ms timer the rest of the app sees, not raw Firestore values.
    const timer = toSession(snapshot)!.timer
    const patch = next(timer)
    if (patch === null) return // nothing to do; leave the document untouched

    tx.update(ref, { timer: patch, updatedAt: serverTimestamp() })
  })
}

export async function pauseTimer(sessionId: string): Promise<void> {
  await updateTimer(sessionId, (timer) => {
    if (timer.status !== "running") return null

    // `startedAt` comes from the server but `Date.now()` is this client's clock,
    // so the difference can be negative when the two disagree. Clamp both ends,
    // or a skewed clock would bank negative elapsed time and the timer would
    // resume with MORE time left than its duration.
    const segment = timer.startedAt !== null ? Math.max(Date.now() - timer.startedAt, 0) : 0
    const elapsedMs = Math.min(Math.max(timer.elapsedMs + segment, 0), timer.durationMs)

    return { status: "paused", startedAt: null, durationMs: timer.durationMs, elapsedMs }
  })
}

export async function resumeTimer(sessionId: string): Promise<void> {
  await updateTimer(sessionId, (timer) => {
    // Resuming an already-running timer would reset `startedAt` while leaving
    // `elapsedMs` untouched, discarding the time already run — a double-click
    // four minutes into a ten-minute round would jump every client back to 10:00.
    if (timer.status === "running") return null

    return {
      status: "running",
      startedAt: serverTimestamp(),
      durationMs: timer.durationMs,
      elapsedMs: timer.elapsedMs,
    }
  })
}

/**
 * Adds or removes time from the clock the room is watching.
 *
 * `durationMs` is what moves, not `elapsedMs`: the round gets longer or shorter,
 * and the time already run stays run. A transaction for the same reason pause and
 * resume are — the value it changes is the one it has to read first, and a
 * facilitator pressing + twice quickly must add two minutes, not the same minute
 * twice.
 *
 * Clamped so the duration can never fall below what has already elapsed, which
 * would make the remaining time negative and the clock count upwards.
 *
 * `startedAt` is carried forward as the epoch-ms number the mapper read back,
 * not re-stamped with `serverTimestamp()`. Re-stamping would restart the running
 * segment and hand the round its elapsed time back; the number written here IS
 * the server's own instant, read inside this transaction, so nothing drifts.
 */
export async function adjustTimer(sessionId: string, deltaMs: number): Promise<void> {
  await updateTimer(sessionId, (timer) => {
    // Nothing to lengthen: an idle clock has no duration yet, and the caller
    // edits the round's plan instead.
    if (timer.status === "idle") return null

    const durationMs = Math.max(timer.durationMs + deltaMs, timer.elapsedMs)
    if (durationMs === timer.durationMs) return null

    return {
      status: timer.status,
      startedAt: timer.startedAt,
      durationMs,
      elapsedMs: timer.elapsedMs,
    }
  })
}

/* ----------------------------------------------------------------- archive */

export async function removeSession(
  sessionId: string,
  callerId: string,
  kind: RemovalKind
): Promise<void> {
  await updateDoc(sessionDoc(sessionId), {
    removedAt: serverTimestamp(),
    removedAs: kind,
    removedBy: callerId,
    updatedAt: serverTimestamp(),
  })
}

export async function restoreSession(sessionId: string): Promise<void> {
  await updateDoc(sessionDoc(sessionId), {
    ...NOT_REMOVED,
    updatedAt: serverTimestamp(),
  })
}
